// Public marketing page. Rendered per locale (pt at `/`, en at `/en`,
// es at `/es`) — the route wraps it in a LocaleProvider, so all copy
// comes from the dictionary and the <head> carries per-locale canonical
// + hreflang alternates.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Head } from "vite-react-ssg";

import { Button } from "@/components/ui/Button";
import { PlanCards } from "@/components/marketing/PlanCards";
import { PricingToggle } from "@/components/marketing/PricingToggle";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { SiteNav } from "@/components/marketing/SiteNav";
import { usePlans, type BillingPeriod } from "@/hooks/usePlans";
import { LOCALES, LOCALE_HREFLANG, localeUrl } from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";

// Section icons stay in code (not translated).
const FEATURE_ICONS = ["🔭", "🧠", "⚔", "📑"];

export function LandingPage() {
  const navigate = useNavigate();
  const locale = useLocale();
  const d = useDict();
  const t = d.landing;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const plans = usePlans();
  const [period, setPeriod] = useState<BillingPeriod>("monthly");

  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={localeUrl(locale)} />
        <meta property="og:url" content={localeUrl(locale)} />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        {LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LOCALE_HREFLANG[loc]}
            href={localeUrl(loc)}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={localeUrl("pt")} />
      </Head>

      {/* ── Nav ─────────────────────────────────────────────── */}
      <SiteNav />

      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="pt-16 sm:pt-24 pb-12 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-4">
            {t.hero.eyebrow}
          </div>
          <h1
            className="font-display font-bold text-content mb-6 leading-[1.1]"
            style={{ fontSize: "clamp(32px, 6vw, 68px)" }}
          >
            {t.hero.tagline}
          </h1>
          <p className="font-sans text-[16px] sm:text-[18px] text-muted max-w-[700px] mx-auto leading-[1.6] mb-8">
            {t.hero.subline}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              variant="primary"
              onClick={() => navigate(isAuthed ? "/app" : "/login")}
            >
              {isAuthed ? t.hero.ctaPrimaryAuthed : t.hero.ctaPrimary}
            </Button>
            <a
              href="#pricing"
              className="inline-flex items-center px-5 py-3 min-h-[44px] rounded text-content font-mono text-[13px] font-semibold cursor-pointer"
              style={{ background: "transparent", border: "1px solid rgb(var(--color-stroke))" }}
            >
              {t.hero.seePricing}
            </a>
          </div>
          <div className="font-mono text-[10px] text-dim mt-6 tracking-[0.05em]">
            {t.hero.socialProof}
          </div>
        </div>
      </section>

      {/* ── Problem statement ───────────────────────────────── */}
      <section className="py-12 sm:py-16 px-4 sm:px-6" style={{ background: "rgb(var(--color-bg))" }}>
        <div className="max-w-[1000px] mx-auto">
          <div className="font-mono text-[11px] text-danger uppercase tracking-[0.2em] mb-3 text-center">
            {t.problem.eyebrow}
          </div>
          <h2 className="font-display text-[24px] sm:text-[32px] font-bold text-content mb-8 text-center leading-[1.2]">
            {t.problem.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t.problem.points.map((p, i) => (
              <div
                key={i}
                className="p-4 rounded font-sans text-[14px] text-muted leading-[1.6]"
                style={{
                  background: "rgb(var(--color-bg2))",
                  border: "1px solid rgb(var(--color-stroke))",
                }}
              >
                <span className="text-danger mr-2">✕</span>
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform overview ───────────────────────────────── */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-3">
              {t.platform.eyebrow}
            </div>
            <h2 className="font-display text-[26px] sm:text-[36px] font-bold text-content mb-4 leading-[1.2]">
              {t.platform.title}
            </h2>
            <p className="font-sans text-[14px] sm:text-[15px] text-muted max-w-[640px] mx-auto leading-[1.6]">
              {t.platform.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {t.platform.features.map((f, i) => (
              <div
                key={f.title}
                className="p-5 sm:p-6 rounded"
                style={{
                  background: "rgb(var(--color-bg2))",
                  border: "1px solid rgb(var(--color-stroke))",
                }}
              >
                <div className="text-[28px] sm:text-[32px] mb-3">
                  {FEATURE_ICONS[i]}
                </div>
                <h3 className="font-display text-[18px] font-bold text-content mb-2">
                  {f.title}
                </h3>
                <p className="font-sans text-[13px] text-muted leading-[1.6]">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────── */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ background: "rgb(var(--color-bg))" }}>
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-3">
              {t.how.eyebrow}
            </div>
            <h2 className="font-display text-[26px] sm:text-[36px] font-bold text-content leading-[1.2]">
              {t.how.title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.how.steps.map((s, i) => (
              <div key={s.title} className="text-center">
                <div
                  className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center font-display text-[22px] font-bold"
                  style={{
                    background: "rgb(var(--color-accent-bg))",
                    border: "2px solid rgb(var(--color-accent))",
                    color: "rgb(var(--color-accent))",
                  }}
                >
                  {i + 1}
                </div>
                <h3 className="font-display text-[18px] font-bold text-content mb-2">
                  {s.title}
                </h3>
                <p className="font-sans text-[13px] text-muted leading-[1.6]">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI block ────────────────────────────────────────── */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-3">
            {t.ai.eyebrow}
          </div>
          <h2 className="font-display text-[26px] sm:text-[36px] font-bold text-content mb-6 leading-[1.2]">
            {t.ai.title}
          </h2>
          <p className="font-sans text-[15px] sm:text-[16px] text-muted leading-[1.7]">
            {t.ai.body}
          </p>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────── */}
      <section id="pricing" className="py-12 sm:py-20 px-4 sm:px-6" style={{ background: "rgb(var(--color-bg))" }}>
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <div className="font-mono text-[11px] text-accent uppercase tracking-[0.2em] mb-3">
              {t.pricing.eyebrow}
            </div>
            <h2 className="font-display text-[26px] sm:text-[36px] font-bold text-content mb-3 leading-[1.2]">
              {t.pricing.title}
            </h2>
            <p className="font-sans text-[14px] text-muted">
              {t.pricing.subtitle}
            </p>
          </div>

          <PricingToggle value={period} onChange={setPeriod} />
          <PlanCards plans={plans} period={period} />
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="font-display text-[28px] sm:text-[38px] font-bold text-content mb-4 leading-[1.2]">
            {t.finalCta.title}
          </h2>
          <p className="font-sans text-[15px] sm:text-[16px] text-muted mb-8 leading-[1.6]">
            {t.finalCta.body}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(isAuthed ? "/app" : "/login")}
          >
            {isAuthed ? t.finalCta.buttonAuthed : t.finalCta.button}
          </Button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
